import React from "react";
import { Tooltip, Typography, useTheme } from "@mui/material";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import { PaperElevation } from "shared-ts-ui-elevation";
import { useWindowWidthComparer } from "shared-ts-ui-responsive";
import {
  AppNavigationLayout,
  useNavigationType,
  Variant,
} from "shared-ts-ui-app-layout";
import { AirmontLogoImg } from "shared-ts-ui-airmont";
import RoofingRoundedIcon from "@mui/icons-material/RoofingRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { ActionGroup, LinkItem, RouteItem } from "shared-ts-ui-action";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import FireExtinguisherRoundedIcon from "@mui/icons-material/FireExtinguisherRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import { useTranslation } from "react-i18next";
import { useUser } from "shared-ts-utils-authentication";
import { useOidcPaths } from "firefly-shared-ts-authentication-oidc";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import { useAppInfo } from "shared-ts-app-info";
import { useAbnormalBurnsPageEnabled } from "./useAbnormalBurnsPageEnabled";
import { PostHogTrackTanstackReactRouterPageView } from "shared-ts-posthog";
import { useChangeThemeAction } from "shared-ts-ui-app-theme-provider";
import { useNavigation } from "shared-ts-tanstack-react-router-navigation";
import { Outlet } from "@tanstack/react-router";
import { useFlueBurnsPageEnabled } from "./useFlueBurnsPageEnabled";
import { useTaskPlanningPageEnabled } from "./useTaskPlanningPageEnabled";
import { useGenerateErrorPageEnabled } from "./useGenerateErrorPageEnabled";
import { SentryTanstackRouterIntegration } from "shared-ts-sentry";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useMunicipalityActionGroup } from "firefly-shared-ts-domain";
import {
  useCustomerEnvironmentActionGroup,
  useCustomerEnvironmentContext,
} from "firefly-shared-ts-customer-environment";

export const RootComponent = () => {
  const theme = useTheme();
  const appInfo = useAppInfo();
  const changeThemeAction = useChangeThemeAction();
  const user = useUser();
  const windowWidthComparer = useWindowWidthComparer();
  const { t } = useTranslation("chimney-insights-ts-translations");
  const { t: t_firefly_shared_domain } = useTranslation(
    "firefly-shared-ts-domain"
  );
  const { t: t_shared_ts_release } = useTranslation("shared-ts-release");
  const hasAdminRole = user.hasRole("Admin");
  const oidcPaths = useOidcPaths();
  const manageUsersHref = oidcPaths?.IdentityManageUsersPath;
  const abnormalBurnsPageEnabled = useAbnormalBurnsPageEnabled();
  const flueBurnsPageEnabled = useFlueBurnsPageEnabled();
  const taskPlanningPageEnabled = useTaskPlanningPageEnabled();
  const generateErrorPageEnabled = useGenerateErrorPageEnabled();

  const appLayoutVariant: Variant =
    (localStorage.getItem("AppLayout.variant") as Variant) ?? "flat";
  const { environment } = useCustomerEnvironmentContext();
  const customerEnvironmentActionGroup = useCustomerEnvironmentActionGroup();
  const municipalitiesActionGroup = useMunicipalityActionGroup();
  const { navigationType } = useNavigationType();
  const { navigationItems, handleSelected } = useNavigation(
    [
      {
        type: "Component",
        render: (navigationType) => (
          <AirmontLogoImg
            size={navigationType === "drawer" ? "standard" : "small"}
            sx={{ m: 1 }}
          />
        ),
      },
      {
        type: "Component",
        render: (navigationType) => (
          <Typography
            variant={"button"}
            color={"secondary"}
            align={"center"}
            sx={{
              paddingTop: theme.spacing(1),
              textTransform: "unset",
              fontSize: navigationType === "drawer" ? "2em" : undefined,
            }}
          >
            {t("Chimney Insights")}
          </Typography>
        ),
      },
      {
        type: "Component",
        render: (navigationType) => (
          <Tooltip
            title={t_shared_ts_release("Release")}
            arrow
            placement={"right"}
          >
            <Typography
              variant={"button"}
              align={"center"}
              color={"text.secondary"}
              sx={{
                textTransform: "unset",
                fontSize: navigationType === "rail" ? "10px" : undefined,
                fontWeight: navigationType === "rail" ? 500 : undefined,
              }}
            >
              {appInfo.release}
            </Typography>
          </Tooltip>
        ),
      },
      {
        type: "RouteItem",
        selectable: true,
        name: t("Overview"),
        route: "/",
        icon: <AnalyticsRoundedIcon />,
        selected: false,
      } as RouteItem,
      {
        type: "ActionGroup",
        name: t("Fire Prevention"),
        nameBreakable: t("Fire Prevention:breakable"),
        reference: "fire-prevention",
        icon: <FireExtinguisherRoundedIcon />,
        selected: true,
        preferences: {
          displayInline: true,
          displayGroupNode: true,
          hideGroupNodeSelected: false,
          medium: {
            displayInline: false,
            displayGroupNode: true,
            hideGroupNodeSelected: true,
          },
        },
        items: [
          {
            type: "RouteItem",
            selectable: true,
            name: t("Chimney Status"),
            route: "/flue-status",
            icon: <RoofingRoundedIcon />,
            selected: false,
          } as RouteItem,
          {
            type: "RouteItem",
            selectable: true,
            name: t("Need for Sweep"),
            nameBreakable: t("Need for Sweep:breakable"),
            route: "/need-for-sweep",
            icon: <PendingActionsRoundedIcon />,
            selected: false,
          } as RouteItem,
          {
            type: "RouteItem",
            selectable: true,
            name: t("Sweep Capacity"),
            nameBreakable: t("Sweep Capacity:breakable"),
            route: "/sweep-capacity",
            icon: <CalendarTodayRoundedIcon />,
            selected: false,
          } as RouteItem,
          taskPlanningPageEnabled
            ? ({
                type: "RouteItem",
                selectable: true,
                name: t("Task Planning"),
                nameBreakable: t("Task Planning:breakable"),
                route: "/task-planning",
                icon: <CalendarMonthRoundedIcon sx={{ opacity: 0.3 }} />,
                selected: false,
              } as RouteItem)
            : undefined,
          abnormalBurnsPageEnabled
            ? ({
                type: "RouteItem",
                selectable: true,
                name: t("Abnormal Burns"),
                route: "/abnormal-burns",
                icon: <FireExtinguisherRoundedIcon sx={{ opacity: 0.3 }} />,
                selected: false,
              } as RouteItem)
            : undefined,
          flueBurnsPageEnabled
            ? ({
                type: "RouteItem",
                selectable: true,
                name: t_firefly_shared_domain("Burns"),
                route: "/flue-burns",
                icon: <FireExtinguisherRoundedIcon sx={{ opacity: 0.3 }} />,
                selected: false,
              } as RouteItem)
            : undefined,
        ],
      } as ActionGroup,
      {
        type: "RouteItem",
        selectable: true,
        route: "/sensor-status",
        name: t("Sensor Status"),
        icon: <SensorsRoundedIcon />,
        selected: false,
      } as RouteItem,
      { type: "Spacer" },
      customerEnvironmentActionGroup,
      municipalitiesActionGroup,
      {
        type: "ActionGroup",
        icon: <MenuRoundedIcon />,
        preferences: {
          displayInline: true,
        },
        items: [
          {
            type: "ActionGroup",
            name: t("Settings"),
            icon: <SettingsRoundedIcon />,
            preferences: {
              displayInline: false,
              hideSelected: true,
            },
            items: [
              {
                type: "RouteItem",
                selectable: true,
                route: "/user",
                name: `${t("User Settings")} (${user.name})`,
                icon: <PersonRoundedIcon />,
                selected: false,
              } as RouteItem,
              {
                type: "RouteItem",
                selectable: true,
                route: "/organization",
                name: `${t("Environment Settings")} (${environment?.name})`,
                icon: <ApartmentRoundedIcon />,
                selected: false,
              } as RouteItem,
              hasAdminRole
                ? ({
                    type: "LinkItem",
                    name: t("Administrate Users"),
                    icon: <PeopleRoundedIcon />,
                    href: manageUsersHref,
                  } as LinkItem)
                : undefined,
            ],
          } as ActionGroup,
          changeThemeAction,
          generateErrorPageEnabled
            ? ({
                type: "RouteItem",
                selectable: true,
                route: "/generate-error",
                name: "Generate Error",
                icon: <ErrorOutlineRoundedIcon />,
                selected: false,
              } as RouteItem)
            : undefined,
          {
            type: "ActionGroup",
            name: t("Help"),
            icon: <HelpRoundedIcon />,
            preferences: {
              displayInline: false,
              hideSelected: true,
              layoutDirection: "row",
            },
            items: [
              {
                type: "LinkItem",
                name: `${appInfo.name} - ${t("user guide")}`,
                href: "/docs/index.html",
              } as LinkItem,
              {
                type: "LinkItem",
                name: `${t("Registration of Chimney Sensor")} - ${t(
                  "user guide"
                )}`,
                href: "https://airmontstorage.blob.core.windows.net/public-firefly/userguide-register-chimney-sensor_no.pdf",
              } as LinkItem,
              {
                type: "LinkItem",
                name: `${t("Registration of Chimney Sensor")} - ${t("video")}`,
                href: "https://airmontstorage.blob.core.windows.net/public-firefly/register-chimney-sensor_no.m4v",
              } as LinkItem,
            ],
          } as ActionGroup,
          {
            type: "RouteItem",
            selectable: true,
            route: "/about",
            name: `${t("About")} ${t("Chimney Insights")}`,
            icon: <InfoRoundedIcon />,
            selected: false,
          } as RouteItem,
        ],
      } as ActionGroup,
    ],
    [
      windowWidthComparer,
      changeThemeAction,
      environment,
      customerEnvironmentActionGroup,
      municipalitiesActionGroup,
      t,
    ],
    navigationType
  );

  return (
    <SentryTanstackRouterIntegration>
      <AppNavigationLayout
        variant={appLayoutVariant}
        forceBackgroundForAllVariants={true}
        navigationItems={navigationItems}
        main={
          <PaperElevation elevation={1}>
            <Outlet />
          </PaperElevation>
        }
        onNavigationItemSelected={handleSelected}
      />
      <PostHogTrackTanstackReactRouterPageView />
      <TanStackRouterDevtools />
    </SentryTanstackRouterIntegration>
  );
};
